import {
  renderVoiceCopy,
  type Voice,
  connectorDeviceListResponseSchema,
  connectorEnrollmentCodeResponseSchema,
  currentRemoteSessionResponseSchema,
  GBTICKET_REMOTE_PROTOCOL,
  okResponseSchema,
  remoteConnectLaunchResponseSchema,
  remoteSessionSchema,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, apiFetch } from '@/lib/api';

/** Browser may only open the Mitarbeiterportal Connector protocol — never rustdesk:// or a password URI. */
export function isGbTicketRemoteProtocolUrl(url: string): boolean {
  const trimmed = url.trim();
  return (
    trimmed.startsWith(GBTICKET_REMOTE_PROTOCOL) &&
    !trimmed.includes('?') &&
    !trimmed.toLowerCase().includes('password')
  );
}

export function connectLaunchErrorMessage(error: unknown, voice: Voice): string {
  if (!(error instanceof ApiRequestError)) {
    return 'Verbindung konnte nicht gestartet werden.';
  }

  const { statusCode, code } = error.error;
  if (code === 'INVALID_PROTOCOL_URL') {
    return 'Ungültige Startadresse vom Server.';
  }
  if (
    statusCode === 409 ||
    statusCode === 410 ||
    code === 'REMOTE_SESSION_NOT_ACTIVE' ||
    code === 'REMOTE_SESSION_EXPIRED' ||
    code === 'REMOTE_SESSION_CREDENTIALS_REMOVED'
  ) {
    return 'Die Sitzung ist nicht mehr aktiv.';
  }
  if (statusCode === 403 && code === 'REMOTE_SESSION_NOT_ASSIGNED') {
    return 'Verbinden darf nur der Agent, dem das Ticket zugewiesen ist.';
  }
  if (statusCode >= 400 && statusCode < 500) {
    return renderVoiceCopy('helpdesk.permission.action.denied', voice);
  }
  return 'Verbindung konnte nicht gestartet werden.';
}

export const remoteSessionQueryKey = (ticketRef: string) =>
  ['remote-support', 'ticket', ticketRef] as const;
export const myConnectorDevicesQueryKey = ['remote-support', 'devices', 'mine'] as const;

export function useCurrentRemoteSession(ticketRef: string, enabled = true) {
  return useQuery({
    queryKey: remoteSessionQueryKey(ticketRef),
    queryFn: () =>
      apiFetch(
        `/remote-support/tickets/${ticketRef}/sessions/current`,
        currentRemoteSessionResponseSchema,
      ),
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.session?.status;
      return status === 'REQUESTED' || status === 'ACTIVE' ? 10_000 : false;
    },
  });
}

export function useRequestRemoteSession(ticketRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/remote-support/tickets/${ticketRef}/sessions`, remoteSessionSchema, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: remoteSessionQueryKey(ticketRef) });
    },
  });
}

export function useEndRemoteSession(ticketRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      apiFetch(`/remote-support/sessions/${sessionId}/end`, remoteSessionSchema, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: remoteSessionQueryKey(ticketRef) });
    },
  });
}

/** Starts the agent connector via a nonce-only protocol URL. Never caches secrets. */
export function useConnectLaunch(ticketRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const result = await apiFetch(
        `/remote-support/sessions/${sessionId}/connect-launch`,
        remoteConnectLaunchResponseSchema,
        { method: 'POST' },
      );
      if (!isGbTicketRemoteProtocolUrl(result.protocolUrl)) {
        throw new ApiRequestError({
          statusCode: 422,
          code: 'INVALID_PROTOCOL_URL',
          message: 'Ungültige Startadresse vom Server.',
          timestamp: new Date().toISOString(),
        });
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: remoteSessionQueryKey(ticketRef) });
    },
  });
}

export function useMyConnectorDevices(enabled = true) {
  return useQuery({
    queryKey: myConnectorDevicesQueryKey,
    queryFn: () => apiFetch('/remote-support/devices/mine', connectorDeviceListResponseSchema),
    enabled,
  });
}

export function useCreateEnrollmentCode() {
  return useMutation({
    mutationFn: () =>
      apiFetch('/remote-support/enrollment-codes', connectorEnrollmentCodeResponseSchema, {
        method: 'POST',
      }),
  });
}

export function useRevokeConnectorDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/remote-support/devices/${id}`, okResponseSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myConnectorDevicesQueryKey });
    },
  });
}
